// PropertyModal.jsx
import { Alert, View, Flex, TextField, TextAreaField, Button } from '@aws-amplify/ui-react';
import SimpleDialog from '../common/SimpleDialog';
import { normalizeCode, slugFromName } from '../../utils/codeUtils';
import { useEffect, useState, useRef, useMemo } from "react";
import { normalizeGraphQLError } from '../../utils/graphqlErrors';
import { getFieldError } from '../../utils/formErrors';

export default function PropertyModal({ open, onClose, onCreated, createPropertyFn }) {
  const maxNameLen = 80, maxNotesLen = 500;
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const codeDirtyRef = useRef(false);

  function markCodeDirty(v = true) { codeDirtyRef.current = v; }

  // Auto-generate code from name unless user has edited code
  useEffect(() => {
    if (!codeDirtyRef.current) setCode(slugFromName(name));
  }, [name]);

  const [touched, setTouched] = useState({
    name: false,
    code: false,
    notes: false,
  });

  // central validation, like Add Device page
  const validation = useMemo(() => {
    const v = {};
    const cleanName = name.trim();
    const cleanCode = normalizeCode(code);
    const cleanNotes = notes.trim();
    const cleanAddress = address.trim();

    if (!cleanName) v.name = 'Name is required.';
    else if (cleanName.length > maxNameLen)
      v.name = `Keep name under ${maxNameLen} characters.`;

    if (!cleanCode) v.code = 'Code is required.';
    else if (cleanCode.length < 2 || cleanCode.length > 32)
      v.code = 'Code must be 2–32 chars.';

    if (cleanNotes && cleanNotes.length > maxNotesLen)
      v.notes = `Notes must be ≤ ${maxNotesLen} chars.`;

    if (cleanAddress && cleanAddress.length > maxNotesLen)
      v.address = `Address must be ≤ ${maxNotesLen} chars.`;

    return v;
  }, [name, code, notes, address]);

  const hasErrors = Object.values(validation).some(Boolean);

  async function handleSubmit(e) {
    e.preventDefault();

    setTouched((t) => ({
      ...t,
      name: true,
      code: true,
      notes: true,
    }));

    if (hasErrors) return; // don’t submit if validation failed

    const input = {
      code: normalizeCode(code),
      name: name.trim(),
      address: address.trim() || null,
      notes: notes.trim() || null,
    };

    setSubmitting(true);
    try {
      const created = await createPropertyFn(input);     // caller passes the actual GraphQL call
      onCreated?.(created);                // let parent select it in Add Device form
      
      setName("");
      setCode("");
      setAddress("");
      setNotes("");
      setTouched({ name: false, code: false, notes: false });
      setErrors({});
      onClose?.();
    } catch (err) {
      console.error('Failed to create property:', err);
      const mapped = normalizeGraphQLError(err);

      setErrors(mapped);
    } finally {
      setSubmitting(false);
    }
  }

  const codeError = getFieldError({
    field: "code",
    touched,
    validation,
    errors
  });

  const nameError = getFieldError({
    field: "name",
    touched,
    validation,
    errors
  });

  const notesError = getFieldError({
    field: "notes",
    touched,
    validation,
    errors
  });

  const addressError = getFieldError({
    field: "address",
    touched,
    validation,
    errors
  });
  return (
    <SimpleDialog open={open} onClose={onClose} title="New Property">
      <View as="form" onSubmit={handleSubmit} noValidate>
        <Flex direction="column" gap="0.75rem">
          {errors.global && (
            <Alert
              variation="error"
              isDismissible={true}
              onDismiss={() => setErrors((e) => ({ ...e, global: undefined }))}
              heading="Couldn't create property"
            >
              {errors.global}
            </Alert>
          )}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              markCodeDirty(false);   // keep auto-sync until user edits code
            }}
            placeholder="e.g., Glenview Apartments"
            onBlur={() => setTouched((t) => ({ ...t, name: true }))}
            hasError={!!nameError}
            errorMessage={nameError || undefined}
            isRequired
          />

          <TextField
            label="Code"
            value={code}
            onChange={(e) => { 
              setCode(e.target.value);
              markCodeDirty(true);
              if (errors.code) setErrors(prev => ({ ...prev, code: undefined, global: undefined })); 
            }}
            placeholder="e.g., PROP-GLEN"
            descriptiveText="Enter your internal code if you have one."
            onBlur={
              () => {
                setTouched((t) => ({ ...t, code: true }));
              }
            }
            hasError={!!codeError}
            errorMessage={codeError || undefined}
            isRequired
          />

          <TextField
            label="Address (optional)"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, address: true }))}
            hasError={!!addressError}
            errorMessage={addressError || undefined}
          />

          <TextAreaField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, notes: true }))}
            hasError={!!notesError}
            errorMessage={notesError || undefined}
            rows={3}
          />

          <Flex justifyContent="flex-end" gap="0.5rem" marginTop="0.5rem">
            <Button type="button" onClick={onClose} variation="link">Cancel</Button>
            <Button 
              type="submit" 
              isDisabled={submitting}
              variation={(submitting) ? "secondary" : "primary"}
            >
              {submitting ? "Submitting…" : "Create"}
            </Button>
          </Flex>
        </Flex>
      </View>
    </SimpleDialog>
  );
}
